import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, verifyOrder, deleteOrder, fetchOrderById } from '../api/api'; 
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import 'react-toastify/dist/ReactToastify.css';
import { 
    FaEye, FaCheckCircle, FaTimesCircle, FaEdit, 
    FaUser, FaPhone, FaMapMarkerAlt, FaBoxOpen, FaStore, FaRupeeSign 
} from 'react-icons/fa';

interface MainUser {
    id: string;
    firstName: string;
    lastName?: string;
    email?: string;
    mobile?: string;
}

export interface SoldOrder {
    id: string;
    orderId?: string;
    mainUser?: MainUser; 
    alternateMobile?: string;
    houseNumber?: string;
    streetArea?: string;
    landmark?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
    medicine: string;
    medicineUnit?: string;
    medicineQuantity: number;
    medicinePrice: number;
    discountPercentage?: number;
    payableAmount: number;
    status: string;
    sellerUsername?: string;
    createdAt?: string;
}

// --- Order Detail Modal (Same as before) ---
const OrderDetailsModal: React.FC<{ order: SoldOrder; onClose: () => void }> = ({ order, onClose }) => {
    const fName = order.mainUser?.firstName || '';
    const lName = order.mainUser?.lastName || '';
    const nameDisplay = fName ? `${fName} ${lName}` : 'Unknown Customer';
    const mobileDisplay = order.mainUser?.mobile || 'N/A';
    
    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="bg-purple-600 p-5 flex justify-between items-center text-white">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 rounded-lg">
                           <FaBoxOpen size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Order Details</h2>
                            <p className="text-xs text-purple-100 opacity-80">ID: {order.id}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
                        <FaTimesCircle size={22}/>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
                    <div className="bg-purple-50 dark:bg-gray-800/50 p-4 rounded-xl border border-purple-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Product Name</span>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                {order.medicine}
                                <span className="text-xs bg-purple-200 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                                    Qty: {order.medicineQuantity}
                                </span>
                            </h3>
                        </div>
                        <div className="text-right">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Total Amount</span>
                            <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 flex items-center">
                                <FaRupeeSign size={18}/> {order.payableAmount}
                            </h3>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-6">
                            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <FaUser className="text-purple-500"/> Customer Info
                                </h4>
                                <div className="space-y-2">
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">{nameDisplay}</p>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm flex items-center gap-2">
                                        <FaPhone size={12} className="text-gray-400"/> {mobileDisplay}
                                    </p>
                                    {order.alternateMobile && (
                                        <p className="text-gray-500 text-xs pl-5">Alt: {order.alternateMobile}</p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <FaStore className="text-orange-500"/> Seller Info
                                </h4>
                                <div className="space-y-2">
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">
                                        {order.sellerUsername ? `@${order.sellerUsername}` : 'Direct/Admin Sale'}
                                    </p>
                                    <span className={`text-xs px-2 py-1 rounded border ${order.sellerUsername ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                                        {order.sellerUsername ? 'Seller Order' : 'Platform Order'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl h-full">
                            <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaMapMarkerAlt className="text-red-500"/> Shipping Address
                            </h4>
                            <div className="text-gray-700 dark:text-gray-300 text-sm space-y-1 leading-relaxed">
                                <p className="font-medium">{order.houseNumber}</p>
                                <p>{order.streetArea}</p>
                                {order.landmark && <p className="text-gray-500 italic">Near {order.landmark}</p>}
                                <p>{order.city}, {order.state}</p>
                                <p className="font-bold text-gray-900 dark:text-white tracking-wide pt-1">PIN: {order.pincode}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-800 dark:text-white transition text-sm font-medium">
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
const UnverifiedOrders: React.FC = () => {
    const [orders, setOrders] = useState<SoldOrder[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState<SoldOrder | null>(null);
    const [isAgent, setIsAgent] = useState<boolean>(false);
    const navigate = useNavigate();

    // 🔥 JWT Decode karke Role check karne ka logic
    useEffect(() => {
        const checkUserRole = () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    }).join(''));
                    
                    const payload = JSON.parse(jsonPayload);
                    const roleStr = JSON.stringify(payload).toUpperCase();
                    
                    // Agar role me AGENT likha hai toh true set kar do
                    if (roleStr.includes('AGENT')) {
                        setIsAgent(true);
                    }
                }
            } catch (e) {
                console.error("Error decoding token", e);
            }
        };
        checkUserRole();
    }, []);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('https://ayushvatika.com/api/admin/unverified-orders', {
            // const response = await fetch('http://localhost:8484/api/admin/unverified-orders', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            setOrders(data);
        } catch (err) {
            console.error("Fetch failed:", err);
            toast.error("Failed to load orders.");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => { loadOrders(); }, []);

    const handleEdit = (orderId: string) => {
        navigate(`/admin/update-order/${orderId}`);
    };

    const handleVerify = async (orderId: string) => {
        if (!window.confirm("Verify Order?")) return;
        try {
            await verifyOrder(orderId);
            toast.success("Verified!");
            setOrders(prev => prev.filter(o => o.id !== orderId));
        } catch (e) { toast.error("Verification failed"); }
    };

    const handleDelete = async (orderId: string) => {
        if (!window.confirm("Delete Order?")) return;
        try {
            await deleteOrder(orderId);
            toast.success("Deleted!");
            setOrders(prev => prev.filter(o => o.id !== orderId));
        } catch (e) { toast.error("Delete failed"); }
    };

    const handleView = async (orderId: string) => {
        try {
            const response = await fetchOrderById(orderId);
            setSelectedOrderDetails(response.data as any);
            setIsModalOpen(true);
        } catch (e) { toast.error("Details fetch failed"); }
    };
    
    return (
        <AdminLayout>
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen text-gray-900 dark:text-white transition-colors duration-200">
                <h1 className="text-3xl font-bold mb-6 text-purple-600 dark:text-purple-400">Unverified Sale Orders</h1>
                
                {!loading && orders.length > 0 ? (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto shadow-xl custom-scrollbar">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-100 dark:bg-gray-700">
                                <tr>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Order ID</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Customer</th>
                                    <th className="py-3 px-4 text-left text-xs text-gray-500 dark:text-gray-300">Product</th>
                                    <th className="py-3 px-4 text-right text-xs text-gray-500 dark:text-gray-300">Total (₹)</th>
                                    <th className="py-3 px-4 text-center text-xs text-gray-500 dark:text-gray-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {orders.map((order) => {
                                     const name = order.mainUser ? `${order.mainUser.firstName} ${order.mainUser.lastName||''}` : 'Unknown';
                                     
                                     return (
                                        <tr key={order.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                            <td className="py-3 px-4 text-sm text-purple-600 dark:text-purple-400">{order.id.substring(0, 8)}...</td>
                                            <td className="py-3 px-4">
                                                <div className="font-medium text-gray-900 dark:text-white">{name}</div>
                                                <div className="text-gray-500 dark:text-gray-400 text-xs">{order.mainUser?.mobile}</div>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{order.medicine}</td>
                                            <td className="py-3 px-4 text-right text-green-600 dark:text-green-400 font-bold">₹{order.payableAmount}</td>
                                            <td className="py-3 px-4 text-center flex justify-center gap-2">
                                                <button onClick={() => handleView(order.id)} className="p-2 bg-blue-600 hover:bg-blue-500 rounded text-white shadow" title="View Details"><FaEye/></button>
                                                <button onClick={() => handleEdit(order.id)} className="p-2 bg-yellow-500 hover:bg-yellow-400 rounded text-white shadow" title="Edit Order"><FaEdit/></button>
                                                
                                                {/* 🔥 SIRF ADMIN/MANAGER KO DIKHENGE YE BUTTONS */}
                                                {!isAgent && (
                                                    <>
                                                        <button onClick={() => handleVerify(order.id)} className="p-2 bg-green-600 hover:bg-green-500 rounded text-white shadow" title="Verify Order"><FaCheckCircle/></button>
                                                        <button onClick={() => handleDelete(order.id)} className="p-2 bg-red-600 hover:bg-red-500 rounded text-white shadow" title="Delete Order"><FaTimesCircle/></button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : <p className="text-gray-500 dark:text-gray-400 text-center">No orders found.</p>}
            </div>
            {isModalOpen && selectedOrderDetails && <OrderDetailsModal order={selectedOrderDetails} onClose={() => setIsModalOpen(false)} />}
            
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f3f4f6; }
                .dark .custom-scrollbar::-webkit-scrollbar-track { background: #1f2937; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 4px; }
            `}</style>
        </AdminLayout>
    );
};

export default UnverifiedOrders;